import React, { useEffect, useState, useRef } from 'react';
import { useAuth } from '../context/AuthContext';
import { Award, BookOpen, Target, Calendar, Printer, CheckCircle, Star, Clock, TrendingUp } from '../components/CustomIcons';

export default function ProgressReport() {
  const { authFetch, user } = useAuth();
  const [profile, setProfile] = useState({});
  const [credentials, setCredentials] = useState([]);
  const [rewardHistory, setRewardHistory] = useState([]);
  const [pathway, setPathway] = useState(null);
  const [loading, setLoading] = useState(true);
  const printRef = useRef(null);

  useEffect(() => {
    Promise.all([
      authFetch('/auth/profile').catch(() => ({ data: {} })),
      authFetch('/credentials').catch(() => ({ data: [] })),
      authFetch('/rewards/history?limit=50').catch(() => ({ data: [] })),
    ]).then(([profileRes, credRes, rewardRes]) => {
      const p = profileRes.data || {};
      setProfile(p);
      setCredentials(credRes.data || []);
      setRewardHistory(rewardRes.data || []);
      const cp = p.current_pathway;
      if (cp) {
        return authFetch(`/courses/pathways/${cp}`).then(r => setPathway(r.data));
      }
    }).catch(e => console.error('report-load error:', e))
      .finally(() => setLoading(false));
  }, [user]); // eslint-disable-line react-hooks/exhaustive-deps

  const completedCourses = rewardHistory.filter(r => r.type === 'course_complete' || r.action === 'course_complete');
  const quizHistory = rewardHistory.filter(r => r.type === 'quiz_pass' || r.action === 'quiz_pass');
  const totalCredits = profile.credit_balance || 0;
  const daysOnProgram = profile.days_in || 0;
  const currentStreak = profile.streak_days || 0;
  const printDate = new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });

  const handlePrint = () => {
    window.print();
  };

  if (loading) {
    return (
      <div style={{ padding: '40px', textAlign: 'center', color: 'var(--text-muted)' }}>
        <div className="skeleton" style={{ width: '300px', height: '24px', margin: '0 auto 12px', borderRadius: '8px' }} />
        <div className="skeleton" style={{ width: '200px', height: '16px', margin: '0 auto', borderRadius: '6px' }} />
      </div>
    );
  }

  return (
    <div style={{ background: 'var(--bg-page)', minHeight: '100vh' }} data-testid="progress-report-page">

      {/* ── Print Controls (hidden on print) ── */}
      <div className="no-print" style={{ padding: '16px 24px', borderBottom: '1px solid var(--border-subtle)', display: 'flex', justifyContent: 'space-between', alignItems: 'center', background: 'var(--bg-surface)', position: 'sticky', top: 0, zIndex: 10 }}>
        <div>
          <h1 style={{ margin: 0, fontFamily: 'Plus Jakarta Sans, sans-serif', fontWeight: 800, fontSize: '20px', color: 'var(--text-primary)' }}>
            Learner Progress Report
          </h1>
          <p style={{ margin: '2px 0 0', fontSize: '13px', color: 'var(--text-muted)' }}>
            Official transcript for {user?.first_name} {user?.last_name}
          </p>
        </div>
        <button onClick={handlePrint}
          style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '10px 20px', background: 'var(--orange)', border: 'none', borderRadius: '10px', cursor: 'pointer', color: '#111', fontFamily: 'Plus Jakarta Sans, sans-serif', fontWeight: 700, fontSize: '14px' }}
          data-testid="print-report-btn">
          <Printer size={16} />
          Print / Save PDF
        </button>
      </div>

      {/* ── Printable Report ── */}
      <div ref={printRef} style={{ maxWidth: '860px', margin: '0 auto', padding: '32px 24px' }} className="print-container">

        {/* Report Header */}
        <div style={{ textAlign: 'center', marginBottom: '32px', paddingBottom: '24px', borderBottom: '2px solid var(--border-subtle)' }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '12px', marginBottom: '16px' }}>
            <div style={{ width: '48px', height: '48px', borderRadius: '12px', background: 'var(--orange)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <Target size={26} color="#111" />
            </div>
            <div style={{ textAlign: 'left' }}>
              <div style={{ fontFamily: 'Plus Jakarta Sans, sans-serif', fontWeight: 800, fontSize: '22px', color: 'var(--text-primary)' }}>
                Assistiv OS
              </div>
              <div style={{ fontSize: '12px', color: 'var(--text-muted)', fontFamily: 'Inter, sans-serif' }}>
                Correctional Education & Workforce Development Platform
              </div>
            </div>
          </div>
          <h2 style={{ fontFamily: 'Plus Jakarta Sans, sans-serif', fontWeight: 700, fontSize: '26px', color: 'var(--text-primary)', margin: '0 0 6px' }}>
            Official Learner Progress Transcript
          </h2>
          <p style={{ color: 'var(--text-secondary)', fontSize: '14px', margin: '0 0 6px' }}>
            Generated: {printDate}
          </p>
        </div>

        {/* Learner Info */}
        <div className="card" style={{ padding: '20px', marginBottom: '20px' }} data-testid="learner-info-section">
          <h3 style={{ fontFamily: 'Plus Jakarta Sans, sans-serif', fontWeight: 700, fontSize: '14px', color: 'var(--orange)', textTransform: 'uppercase', letterSpacing: '0.08em', margin: '0 0 14px' }}>
            Learner Information
          </h3>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '12px' }}>
            {[
              { label: 'Full Name', value: `${user?.first_name || ''} ${user?.last_name || ''}`.trim() },
              { label: 'Student ID', value: profile.student_id || profile._id?.slice(-8)?.toUpperCase() || 'N/A' },
              { label: 'Facility', value: profile.facility || profile.facility_name || 'Assistiv OS Facility' },
              { label: 'Enrollment Date', value: profile.created_at ? new Date(profile.created_at).toLocaleDateString() : 'N/A' },
              { label: 'Days on Program', value: `${daysOnProgram} days` },
              { label: 'Current Pathway', value: pathway?.name || profile.current_pathway || 'Not enrolled' },
            ].map(item => (
              <div key={item.label}>
                <div style={{ fontSize: '11px', color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: '3px' }}>{item.label}</div>
                <div style={{ fontSize: '14px', color: 'var(--text-primary)', fontWeight: 600, fontFamily: 'Plus Jakarta Sans, sans-serif' }}>{item.value}</div>
              </div>
            ))}
          </div>
        </div>

        {/* Summary Stats */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: '12px', marginBottom: '20px' }}>
          {[
            { icon: <Award size={20} />, label: 'Credentials Earned', value: credentials.length, color: '#F97316' },
            { icon: <BookOpen size={20} />, label: 'Courses Completed', value: completedCourses.length, color: '#3B82F6' },
            { icon: <Star size={20} />, label: 'Total Credits', value: totalCredits.toLocaleString(), color: '#22C55E' },
            { icon: <TrendingUp size={20} />, label: 'Day Streak', value: `${currentStreak} days`, color: '#A855F7' },
          ].map(stat => (
            <div key={stat.label} className="card" style={{ padding: '16px', textAlign: 'center' }} data-testid={`stat-${stat.label.toLowerCase().replace(/ /g, '-')}`}>
              <div style={{ color: stat.color, display: 'flex', justifyContent: 'center', marginBottom: '8px' }}>{stat.icon}</div>
              <div style={{ fontFamily: 'Plus Jakarta Sans, sans-serif', fontWeight: 800, fontSize: '22px', color: 'var(--text-primary)', marginBottom: '4px' }}>
                {stat.value}
              </div>
              <div style={{ fontSize: '11px', color: 'var(--text-muted)' }}>{stat.label}</div>
            </div>
          ))}
        </div>

        {/* Current Pathway Progress */}
        {pathway && (
          <div className="card" style={{ padding: '20px', marginBottom: '20px' }} data-testid="pathway-progress-section">
            <h3 style={{ fontFamily: 'Plus Jakarta Sans, sans-serif', fontWeight: 700, fontSize: '14px', color: 'var(--orange)', textTransform: 'uppercase', letterSpacing: '0.08em', margin: '0 0 14px' }}>
              Active Pathway: {pathway.name}
            </h3>
            <div style={{ marginBottom: '12px' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '6px' }}>
                <span style={{ fontSize: '13px', color: 'var(--text-secondary)' }}>Pathway Progress</span>
                <span style={{ fontSize: '13px', fontWeight: 700, color: 'var(--orange)' }}>{profile.pathway_progress || 0}%</span>
              </div>
              <div className="progress-bar">
                <div className="progress-fill" style={{ width: `${profile.pathway_progress || 0}%` }} />
              </div>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: '10px', marginTop: '14px' }}>
              {(pathway.courses || []).map((course, idx) => (
                <div key={course.id || idx} style={{ display: 'flex', gap: '10px', alignItems: 'center', padding: '10px', background: 'var(--bg-elevated)', borderRadius: '8px' }}>
                  <CheckCircle size={16} color={idx < (profile.completed_modules || 0) ? '#22C55E' : 'var(--text-dim)'} />
                  <span style={{ fontSize: '13px', color: 'var(--text-secondary)' }}>{course.title}</span>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Credentials */}
        {credentials.length > 0 && (
          <div className="card" style={{ padding: '20px', marginBottom: '20px' }} data-testid="credentials-section">
            <h3 style={{ fontFamily: 'Plus Jakarta Sans, sans-serif', fontWeight: 700, fontSize: '14px', color: 'var(--orange)', textTransform: 'uppercase', letterSpacing: '0.08em', margin: '0 0 14px' }}>
              Earned Credentials & Certifications
            </h3>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
              {credentials.map((cred, idx) => (
                <div key={cred.id || idx} style={{ display: 'flex', gap: '14px', alignItems: 'center', padding: '12px 14px', background: 'var(--bg-elevated)', borderRadius: '10px', borderLeft: '3px solid var(--orange)' }} data-testid={`credential-${idx}`}>
                  <Award size={20} color="var(--orange)" style={{ flexShrink: 0 }} />
                  <div style={{ flex: 1 }}>
                    <div style={{ fontFamily: 'Plus Jakarta Sans, sans-serif', fontWeight: 600, fontSize: '14px', color: 'var(--text-primary)' }}>{cred.credential_name || cred.name}</div>
                    <div style={{ fontSize: '12px', color: 'var(--text-muted)', marginTop: '2px' }}>
                      {cred.issuer || cred.issuing_org || 'Assistiv OS'} · Issued {cred.issued_date ? new Date(cred.issued_date).toLocaleDateString() : 'N/A'}
                      {cred.credential_id && <span> · ID: {cred.credential_id}</span>}
                    </div>
                  </div>
                  <CheckCircle size={16} color="#22C55E" />
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Recent Activity */}
        {rewardHistory.length > 0 && (
          <div className="card" style={{ padding: '20px', marginBottom: '20px' }} data-testid="activity-section">
            <h3 style={{ fontFamily: 'Plus Jakarta Sans, sans-serif', fontWeight: 700, fontSize: '14px', color: 'var(--orange)', textTransform: 'uppercase', letterSpacing: '0.08em', margin: '0 0 14px' }}>
              Learning Activity (Last 30 Days)
            </h3>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
              {rewardHistory.slice(0, 15).map((item, idx) => (
                <div key={item.id || idx} style={{ display: 'flex', gap: '12px', alignItems: 'center', padding: '8px 10px', borderRadius: '8px', background: idx % 2 === 0 ? 'var(--bg-elevated)' : 'transparent' }}>
                  <Clock size={13} style={{ color: 'var(--text-dim)', flexShrink: 0 }} />
                  <span style={{ flex: 1, fontSize: '13px', color: 'var(--text-secondary)' }}>{item.description || item.action || 'Activity'}</span>
                  <span style={{ fontSize: '12px', color: 'var(--orange)', fontWeight: 600 }}>+{item.credits || item.points || 0} credits</span>
                  <span style={{ fontSize: '11px', color: 'var(--text-dim)' }}>
                    {item.created_at ? new Date(item.created_at).toLocaleDateString() : ''}
                  </span>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Signature block */}
        <div style={{ marginTop: '40px', paddingTop: '24px', borderTop: '2px solid var(--border-subtle)', display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: '20px' }}>
          <div>
            <p style={{ fontSize: '12px', color: 'var(--text-muted)', margin: '0 0 4px' }}>
              This document is an official Assistiv OS learner transcript. Credential verification available at assistivos.demosforclients.com/verify
            </p>
            <p style={{ fontSize: '11px', color: 'var(--text-dim)', margin: 0 }}>
              Generated on {printDate} · Assistiv OS v1.0 · Powered by Tech Tradesmith Institute
            </p>
          </div>
          <div style={{ textAlign: 'right' }}>
            <div style={{ width: '160px', borderTop: '1px solid var(--border-subtle)', paddingTop: '8px', marginLeft: 'auto' }}>
              <p style={{ fontSize: '11px', color: 'var(--text-muted)', margin: 0 }}>Authorized Signature</p>
            </div>
          </div>
        </div>
      </div>

      {/* Print CSS */}
      <style>{`
        @media print {
          .no-print { display: none !important; }
          .app-layout { display: block !important; }
          .main-content { padding-top: 0 !important; }
          body { background: white !important; color: black !important; }
          .card { border: 1px solid #ccc !important; background: white !important; box-shadow: none !important; }
          .progress-bar { border: 1px solid #ccc !important; background: #eee !important; }
          .progress-fill { background: #F97316 !important; }
          :root { --text-primary: #111; --text-secondary: #444; --text-muted: #777; --text-dim: #999; --bg-page: white; --bg-surface: white; --bg-elevated: #f5f5f5; --border-subtle: #ddd; --orange: #F97316; }
        }
      `}</style>
    </div>
  );
}
